// src/pages/Admin/Finance/PaymentOptions/PaymentOptionsEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./PaymentOptionsEdit.module.scss";

const IconArrowLeft = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M14.7 6.3a1 1 0 0 1 0 1.4L11.41 11H20a1 1 0 1 1 0 2h-8.59l3.29 3.3a1 1 0 1 1-1.4 1.4l-5-5a1 1 0 0 1 0-1.4l5-5a1 1 0 0 1 1.4 0z"
      fill="currentColor"
    />
  </svg>
);

const IconSave = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zM7 5h8v4H7V5zm12 14H5V5h1v6h10V5h.17L19 7.83V19z"
      fill="currentColor"
    />
  </svg>
);

const PaymentOptionsEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [instruction, setInstruction] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);

  const goBack = () => navigate("/admin/finance/payment-options");

  const statusLabel = useMemo(
    () => (isActive ? "Active" : "Inactive"),
    [isActive],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErr(null);
        const res = await api.get(`/v1/payment-options/${id}`);
        const data = res.data;

        setName(data.name || "");
        setTitle(data.title || "");
        setDescription(data.description || "");
        setInstruction(data.instruction || "");
        setIsActive(Boolean(data.isActive));
      } catch (error) {
        console.error("Failed to load payment option:", error);
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          "Failed to load payment option.";
        setErr(msg);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSuccess(null);

    if (!name.trim() || !title.trim()) {
      setErr("Name and Title are required.");
      return;
    }

    try {
      setSaving(true);

      const res = await api.put(`/v1/payment-options/${id}`, {
        name: name.trim(),
        title: title.trim(),
        description,
        instruction,
        isActive,
      });

      const updated = res.data;
      setName(updated.name || "");
      setTitle(updated.title || "");
      setDescription(updated.description || "");
      setInstruction(updated.instruction || "");
      setIsActive(Boolean(updated.isActive));

      setSuccess("Payment option updated successfully.");
      navigate(`/admin/finance/payment-options/${id}`, { replace: true });
    } catch (error) {
      console.error("Failed to update payment option:", error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        "Failed to update payment option.";
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={cls.wrap}>
        <header className={cls.header}>
          <div className={cls.headLeft}>
            <h1 className={cls.pageTitle}>Edit Payment Option</h1>
            <p className={cls.pageSub}>
              Update the title, details, and instructions
            </p>
          </div>

          <button type="button" className={cls.backBtn} onClick={goBack}>
            <IconArrowLeft />
            Back
          </button>
        </header>

        <div className={cls.skelWrap}>
          <div className={cls.skel} />
          <div className={cls.skel} />
          <div className={cls.skel} />
        </div>
      </div>
    );
  }

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div className={cls.headLeft}>
          <h1 className={cls.pageTitle}>Edit Payment Option</h1>

          <div className={cls.badges}>
            <span className={`${cls.badge} ${cls.badgeKey}`}>
              {name || "key"}
            </span>
            <span
              className={`${cls.badge} ${isActive ? cls.badgeActive : cls.badgeInactive}`}
            >
              {statusLabel}
            </span>
          </div>
        </div>

        <button type="button" className={cls.backBtn} onClick={goBack}>
          <IconArrowLeft />
          Back
        </button>
      </header>

      {err && <div className={cls.error}>{err}</div>}
      {success && <div className={cls.success}>{success}</div>}

      <form className={cls.form} onSubmit={handleSubmit}>
        <section className={cls.card}>
          <div className={cls.cardHead}>
            <h2 className={cls.cardTitle}>Basics</h2>
            <p className={cls.cardHint}>This is what members will see</p>
          </div>

          <div className={cls.grid2}>
            <div className={cls.field}>
              <label>Name (unique key)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Example: etransfer, cash, crypto"
                autoComplete="off"
              />
              <p className={cls.note}>
                Internal key used in code/config. Keep it stable if possible.
              </p>
            </div>

            <div className={cls.field}>
              <label>Title (display name)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Example: E-Transfer / Bank Transfer"
                autoComplete="off"
              />
              <p className={cls.note}>
                Short and friendly. This shows in the app.
              </p>
            </div>
          </div>

          <div className={cls.field}>
            <label>Description</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description about this payment method..."
            />
          </div>

          <div className={cls.field}>
            <label>Instruction</label>
            <textarea
              rows={5}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="How to pay using this method..."
            />
          </div>
        </section>

        <section className={cls.card}>
          <div className={cls.cardHead}>
            <h2 className={cls.cardTitle}>Status</h2>
            <p className={cls.cardHint}>
              Control whether this option is visible to members
            </p>
          </div>

          <div className={cls.statusRow}>
            <div className={cls.statusLeft}>
              <div className={cls.statusTitle}>Availability</div>
              <div className={cls.statusSub}>
                Set to Active to show it in checkout and payments.
              </div>
            </div>

            <div className={cls.statusPill} role="group" aria-label="Status">
              <button
                type="button"
                className={`${cls.pillBtn} ${isActive ? cls.pillOn : ""}`}
                onClick={() => setIsActive(true)}
                aria-pressed={isActive}
              >
                Active
              </button>
              <button
                type="button"
                className={`${cls.pillBtn} ${!isActive ? cls.pillOff : ""}`}
                onClick={() => setIsActive(false)}
                aria-pressed={!isActive}
              >
                Inactive
              </button>
            </div>
          </div>
        </section>

        <div className={cls.actions}>
          <button
            type="button"
            className={`${cls.actionBtn} ${cls.ghost}`}
            onClick={goBack}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className={`${cls.actionBtn} ${cls.primary}`}
            disabled={saving}
          >
            <IconSave />
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentOptionsEdit;
